import {
  Box,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router';

import { CodigoDePieza } from '@/components/CodigoDePieza';

import {
  enPesos,
  enPorcentaje,
  estaEnOferta,
  precioParaMostrar,
  tieneDescuentoPorBulto,
} from '@/lib/catalogo';
import type { Producto } from '@/tipos';

/**
 * El encabezado, ahora pegajoso. Con `stickyHeader` el color va en la celda y no en la fila: MUI
 * le pone `position: sticky` a cada `th`, y un fondo puesto en el `<tr>` queda por debajo, así
 * que las filas se verían pasar a través del texto.
 */
const ENCABEZADO = {
  bgcolor: '#e60000',
  color: 'white',
  fontWeight: 'bold',
  fontFamily: 'var(--font-roboto-condensed)',
  letterSpacing: '0.06em',
} as const;

interface Props {
  productos: Producto[];
  /** El código del producto → la URL de su foto principal, cuando tiene. */
  fotos: Record<string, string>;
  conPrecios: boolean;
}

export function TablaDeProductos({ productos, fotos, conPrecios }: Props) {
  const navigate = useNavigate();

  function abrir(codigo: string) {
    void navigate(`/catalogo/${encodeURIComponent(codigo)}`);
  }

  return (
    <TableContainer
      component={Paper}
      sx={{
        bgcolor: '#1a1a1a',
        mb: 4,
        // El alto propio es lo que hace posible la cabecera pegajosa. `TableContainer` trae
        // `overflow-x: auto`, y eso convierte también el eje vertical en `auto`: un `sticky`
        // adentro se ancla a este contenedor, no a la ventana. Sin un alto que lo haga
        // desbordar, el contenedor nunca hace scroll y la cabecera no se pega a nada.
        maxHeight: 'calc(100vh - 200px)',
      }}
    >
      <Table stickyHeader sx={{ minWidth: 650 }} aria-label="Tabla de productos del catalogo">
        <TableHead>
          <TableRow>
            {['IMAGEN', 'CODIGO', 'DESCRIPCION', 'CATEGORIA', 'STOCK'].map(columna => (
              <TableCell key={columna} scope="col" sx={ENCABEZADO}>
                {columna}
              </TableCell>
            ))}

            {conPrecios && (
              <TableCell scope="col" sx={{ ...ENCABEZADO, textAlign: 'right' }}>
                PRECIO
              </TableCell>
            )}
          </TableRow>
        </TableHead>

        <TableBody>
          {productos.map(producto => (
            <TableRow
              key={producto.code}
              role="link"
              tabIndex={0}
              aria-label={`Ver producto ${producto.code}: ${producto.description}`}
              onClick={() => abrir(producto.code)}
              onKeyDown={evento => {
                if (evento.key === 'Enter' || evento.key === ' ') {
                  evento.preventDefault();
                  abrir(producto.code);
                }
              }}
              sx={{
                cursor: 'pointer',
                '&:nth-of-type(odd)': { bgcolor: 'rgba(255, 255, 255, 0.05)' },
                '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' },
                // Al pasar por la fila, la miniatura se agranda sobre el resto. A 50 pixeles no
                // se distingue una junta de otra, y abrir la ficha para mirar una foto es
                // perder el lugar en la tabla.
                '&:hover .miniatura-de-fila, &:focus-visible .miniatura-de-fila': {
                  transform: 'scale(3.4)',
                  zIndex: 5,
                  boxShadow: '0 10px 30px rgba(0,0,0,.65)',
                },
                '&:focus-visible': {
                  outline: '2px solid',
                  outlineColor: 'primary.main',
                  outlineOffset: -2,
                },
              }}
            >
              <Fila
                producto={producto}
                foto={fotos[producto.code] ?? null}
                conPrecios={conPrecios}
              />
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function Fila({
  producto,
  foto,
  conPrecios,
}: {
  producto: Producto;
  foto: string | null;
  conPrecios: boolean;
}) {
  const precio = producto.price;
  const { descuento, conOferta, mostrarOferta } = precioParaMostrar(producto);

  return (
    <>
      <TableCell sx={{ overflow: 'visible' }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          {foto ? (
            <Box
              component="img"
              className="miniatura-de-fila"
              src={foto}
              alt={producto.description}
              loading="lazy"
              sx={{
                width: 50,
                height: 50,
                // `contain` y no `cover`: recortar al cuadrado le come las puntas a todo lo
                // alargado -una antena, una varilla-, que es justo lo que hay que reconocer acá.
                objectFit: 'contain',
                bgcolor: '#fff',
                borderRadius: 1,
                position: 'relative',
                transformOrigin: 'left center',
                transition: 'transform .22s cubic-bezier(.22,.61,.36,1), box-shadow .22s ease',
                '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
              }}
            />
          ) : (
            <Box
              sx={{
                width: 50,
                height: 50,
                bgcolor: '#333',
                borderRadius: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography
                variant="caption"
                sx={{ color: 'text.secondary', fontSize: '0.6rem', textAlign: 'center' }}
              >
                Imagen no disponible
              </Typography>
            </Box>
          )}
        </Box>
      </TableCell>

      <TableCell component="th" scope="row">
        <CodigoDePieza codigo={producto.code} sx={{ color: 'primary.light' }} />
      </TableCell>

      <TableCell>
        <Typography variant="body2" sx={{ maxWidth: 200 }}>
          {producto.description}
        </Typography>
      </TableCell>

      <TableCell>
        <Chip
          label={producto.category}
          size="small"
          sx={{
            backgroundColor: '#d32f2f',
            color: 'white',
            fontWeight: 600,
            '&:hover': { backgroundColor: '#b71c1c' },
          }}
        />
      </TableCell>

      <TableCell>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {producto.is_in_stock ? (
            <Chip label="En Stock" color="success" size="small" />
          ) : (
            <Chip label="Sin Stock" color="error" size="small" />
          )}

          {estaEnOferta(producto) && <Chip label="Oferta" color="warning" size="small" />}
          {producto.is_reposition && <Chip label="Reposición" color="secondary" size="small" />}
          {producto.is_incoming && <Chip label="Ingreso" color="info" size="small" />}

          {tieneDescuentoPorBulto(producto.bulk_discount) && (
            <Chip
              label="Descuento Bulto Cerrado"
              size="small"
              sx={{ bgcolor: '#00897b', color: '#fff' }}
            />
          )}
        </Box>
      </TableCell>

      {conPrecios && (
        // `tabular-nums` da a todos los dígitos el mismo ancho: las comas de la columna caen
        // una debajo de la otra y los precios se comparan de un vistazo. A la derecha por lo
        // mismo, que es donde se leen los números.
        <TableCell sx={{ fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>
          {precio === null ? (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Sin precio
            </Typography>
          ) : (
            <Box
              sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, alignItems: 'flex-end' }}
            >
              {mostrarOferta ? (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
                    <Typography
                      variant="body2"
                      component="span"
                      sx={{ color: 'text.secondary', textDecoration: 'line-through' }}
                    >
                      {enPesos(precio)}
                    </Typography>

                    <Typography
                      variant="caption"
                      component="span"
                      sx={{ color: 'warning.main', fontWeight: 'bold' }}
                    >
                      -{enPorcentaje(descuento)}%
                    </Typography>
                  </Box>

                  <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 'bold' }}>
                    {enPesos(conOferta)}
                  </Typography>
                </>
              ) : (
                <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 'bold' }}>
                  {enPesos(precio)}
                </Typography>
              )}
            </Box>
          )}
        </TableCell>
      )}
    </>
  );
}
